import {computed, reactive, type ComputedRef, type Ref} from 'vue';
import {useBreakpoints as useVueUseBreakpoints, useWindowSize} from '@vueuse/core';
import {
  BREAKPOINTS,
  VUETIFY_BREAKPOINT_CONFIG,
  type BreakpointName,
} from '@/commons/constants/breakpoints';

type BreakpointsState = {
  width: Ref<number>;
  height: Ref<number>;

  name: ComputedRef<BreakpointName>;
  mobile: Ref<boolean>;

  xs: Ref<boolean>;
  sm: Ref<boolean>;
  md: Ref<boolean>;
  lg: Ref<boolean>;
  xl: Ref<boolean>;
  xxl: Ref<boolean>;

  xsOnly: Ref<boolean>;
  smOnly: Ref<boolean>;
  mdOnly: Ref<boolean>;
  lgOnly: Ref<boolean>;
  xlOnly: Ref<boolean>;
  xxlOnly: Ref<boolean>;

  smAndUp: Ref<boolean>;
  mdAndUp: Ref<boolean>;
  lgAndUp: Ref<boolean>;
  xlAndUp: Ref<boolean>;
  xxlAndUp: Ref<boolean>;

  smAndDown: Ref<boolean>;
  mdAndDown: Ref<boolean>;
  lgAndDown: Ref<boolean>;
  xlAndDown: Ref<boolean>;

  /** Vuetify-compatible fields used by existing call sites */
  mobileBreakpoint: number;
  thresholds: typeof VUETIFY_BREAKPOINT_CONFIG.thresholds;
  scrollBarWidth: number;
};

/** Reactive view: nested refs unwrap to booleans/numbers (drop-in for `$vuetify.breakpoint`). */
export type BreakpointsApi = {
  [K in keyof BreakpointsState]: BreakpointsState[K] extends Ref<infer V>
    ? V
    : BreakpointsState[K] extends ComputedRef<infer V>
      ? V
      : BreakpointsState[K];
};

let sharedState: BreakpointsState | null = null;
let sharedApi: BreakpointsApi | null = null;

const createBreakpointsState = (): BreakpointsState => {
  const bp = useVueUseBreakpoints(BREAKPOINTS);
  const {width, height} = useWindowSize();

  const smAndUp = bp.greaterOrEqual('sm');
  const mdAndUp = bp.greaterOrEqual('md');
  const lgAndUp = bp.greaterOrEqual('lg');
  const xlAndUp = bp.greaterOrEqual('xl');
  const xxlAndUp = bp.greaterOrEqual('xxl');

  const smAndDown = bp.smaller('md');
  const mdAndDown = bp.smaller('lg');
  const lgAndDown = bp.smaller('xl');
  const xlAndDown = bp.smaller('xxl');

  const xs = bp.smaller('sm');
  const sm = bp.between('sm', 'md');
  const md = bp.between('md', 'lg');
  const lg = bp.between('lg', 'xl');
  const xl = bp.between('xl', 'xxl');
  const xxl = bp.greaterOrEqual('xxl');

  const name: ComputedRef<BreakpointName> = computed(() => {
    if (xxl.value) return 'xxl';
    if (xl.value) return 'xl';
    if (lg.value) return 'lg';
    if (md.value) return 'md';
    if (sm.value) return 'sm';
    return 'xs';
  });

  return {
    width,
    height,

    name,
    mobile: smAndDown,

    xs,
    sm,
    md,
    lg,
    xl,
    xxl,

    xsOnly: xs,
    smOnly: sm,
    mdOnly: md,
    lgOnly: lg,
    xlOnly: xl,
    xxlOnly: xxl,

    smAndUp,
    mdAndUp,
    lgAndUp,
    xlAndUp,
    xxlAndUp,

    smAndDown,
    mdAndDown,
    lgAndDown,
    xlAndDown,

    mobileBreakpoint: VUETIFY_BREAKPOINT_CONFIG.mobileBreakpoint,
    thresholds: VUETIFY_BREAKPOINT_CONFIG.thresholds,
    scrollBarWidth: VUETIFY_BREAKPOINT_CONFIG.scrollBarWidth,
  };
};

/**
 * Shared ref-based breakpoints (Tailwind `@theme` + Vuetify config).
 * Prefer `useBreakpointsApi()` when you need Vuetify-like boolean access without `.value`.
 */
export const useBreakpoints = (): BreakpointsState => {
  if (!sharedState) {
    sharedState = createBreakpointsState();
  }
  return sharedState;
};

/**
 * Singleton reactive API — same object as `$breakpoint` / `$vuetify.breakpoint`.
 * Nested refs are auto-unwrapped (`smAndDown` is `boolean`, not `Ref`).
 */
export const useBreakpointsApi = (): BreakpointsApi => {
  if (!sharedApi) {
    sharedApi = reactive(useBreakpoints()) as BreakpointsApi;
  }
  return sharedApi;
};

/**
 * Keeps Vuetify's Breakpoint service (`init` / `update`) intact, but routes
 * flag / size reads through our Tailwind-aligned singleton.
 */
const VUETIFY_BREAKPOINT_SYNC_KEYS = [
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  'xsOnly',
  'smOnly',
  'mdOnly',
  'lgOnly',
  'xlOnly',
  'smAndUp',
  'mdAndUp',
  'lgAndUp',
  'smAndDown',
  'mdAndDown',
  'lgAndDown',
  'name',
  'height',
  'width',
  'mobile',
  'mobileBreakpoint',
  'thresholds',
  'scrollBarWidth',
] as const satisfies readonly (keyof BreakpointsApi)[];

export const bindVuetifyBreakpoint = (vuetifyBreakpoint: object): BreakpointsApi => {
  const api = useBreakpointsApi();

  for (const key of VUETIFY_BREAKPOINT_SYNC_KEYS) {
    Object.defineProperty(vuetifyBreakpoint, key, {
      configurable: true,
      enumerable: true,
      get: () => api[key],
      // Vuetify's update() writes these — ignore so our matchMedia stays source of truth.
      set: () => undefined,
    });
  }

  // Extra Tailwind-only flags (not part of Vuetify's Breakpoint type)
  for (const key of ['xxl', 'xxlOnly', 'xlAndUp', 'xxlAndUp', 'xlAndDown'] as const) {
    Object.defineProperty(vuetifyBreakpoint, key, {
      configurable: true,
      enumerable: true,
      get: () => api[key],
      set: () => undefined,
    });
  }

  return api;
};

/** @internal test helper */
export const __resetBreakpointsForTests = (): void => {
  sharedState = null;
  sharedApi = null;
};
